import type { EstimateLineItem } from '@/types/estimate';
import type { ProposalLineItem, ProposalOption } from '@/types/proposal';
import { chargesRevenue, rendersOnCustomerDocument } from '@/lib/sales/line-economics';
import { membershipOfferForSelectedLines } from '@/lib/sales/membership-offer';
import { resolveEstimateLineAmounts } from '@/lib/sales/line-item-pricing';
import { optionalKey } from '@/lib/sales/optional-line-key';
import { offerCountedLines, splitOptionalOffers } from '@/lib/sales/service-components';
import { isMembershipLine } from '@notifi/shared-types/membership-line';
import {
  getProposalOptionDepositEligibleTotal,
  proposalOptionDepositAmountOn,
} from '@/components/organisms/proposals/support/proposal-option-math';

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

/**
 * ONE optional add-on the customer can turn on/off — a TOP-LEVEL optional
 * service, never a bundle part on its own. Its billed parts ride with it
 * (`countedLines`), exactly as the individual estimate treats an offer: the
 * operator marking a PART `isOptional` offers it INSIDE a service, it is not a
 * separate thing to buy, and the pricing engine prices a declined service's
 * parts at nothing. Membership rides its own offer block, so it is excluded.
 */
export interface ProposalOptionalOffer {
  /** The stable selection token — shared by the reprice and the accept payload. */
  key: string;
  /** Whether the server can identify this row independently. */
  canToggle: boolean;
  /** The offer's top-level line (its `lineItemId`/`lineKey` go in the payload). */
  parent: ProposalLineItem;
  /** parent + the billed parts that ride with it — what taking the offer adds. */
  countedLines: ProposalLineItem[];
  /** Server LIST subtotal for the complete add-on, or legacy quoted line totals. */
  amount: number | null;
}

/**
 * The optional add-ons for ONE option — the interactive add/remove list. Built
 * from the SAME offer split the estimate uses (`splitOptionalOffers`), so a
 * bundle's parts never surface as their own toggle and a declined service takes
 * its parts with it. Keys come from `optionalKey` over the FULL offer list, so a
 * membership offer sitting among them never shifts another offer's key.
 */
export function proposalOptionOffers(option: ProposalOption): ProposalOptionalOffer[] {
  const offers = splitOptionalOffers(option.lineItems ?? []).offers;
  return offers
    .map((offer, index) => ({ offer, key: optionalKey(offer.parent, index) }))
    .filter(
      ({ offer }) =>
        !isMembershipLine(offer.parent) &&
        rendersOnCustomerDocument(offer.parent) &&
        (offer.parent.name ?? '').trim().length > 0,
    )
    .map(({ offer, key }) => {
      const counted = offerCountedLines(offer, true);
      const amount = offer.parent.displayConfig?.hidePrice
        ? null
        : (offer.parent.optionalSubtotal ??
          round2(counted.reduce((sum, l) => sum + resolveEstimateLineAmounts(l).lineTotal, 0)));
      const canToggle =
        offer.parent.optionalSelectable !== false &&
        (Boolean(offer.parent.lineKey) ||
          option.lineItems.filter(
            (line) => line.isOptional && line.lineItemId === offer.parent.lineItemId,
          ).length === 1);
      return { key, canToggle, parent: offer.parent, countedLines: counted, amount };
    });
}

/** One disclosed charge per selected add-on. A server subtotal includes billed
 * parts omitted from the public projection; never add those visible parts twice.
 * Hidden prices stay within the aggregate scope rather than separate rows. */
export function proposalSelectedOptionalRows(option: ProposalOption): Array<{
  name: string;
  subtotal: number;
}> {
  return proposalOptionOffers(option)
    .filter((offer) => offer.parent.isSelected !== false && offer.amount != null)
    .map((offer) => ({
      name: offer.parent.name,
      subtotal:
        offer.parent.optionalSubtotal ??
        round2(
          offer.countedLines.reduce(
            (sum, line) => sum + (line.lineListTotal ?? line.lineTotal ?? 0),
            0,
          ),
        ),
    }));
}

/** The keys of the offers the option ships with SELECTED — the default picks. */
export function proposalOptionDefaultOfferKeys(option: ProposalOption): Set<string> {
  return new Set(
    proposalOptionOffers(option)
      .filter((o) => o.parent.isSelected !== false)
      .map((o) => o.key),
  );
}

/** Only changed, addressable rows may trigger backend repricing. */
export function proposalOptionSelectionChanges(
  option: ProposalOption,
  selectedKeys: ReadonlySet<string>,
) {
  return proposalOptionOffers(option)
    .filter(
      (offer) =>
        offer.canToggle && selectedKeys.has(offer.key) !== (offer.parent.isSelected !== false),
    )
    .map((offer) => ({
      lineItemId: offer.parent.lineItemId,
      ...(offer.parent.lineKey ? { lineKey: offer.parent.lineKey } : {}),
      isSelected: selectedKeys.has(offer.key),
    }));
}

/**
 * The customer-facing money breakdown for ONE proposal option — the figures the
 * single-scope "Full breakdown" shows under each option's headline price, matching
 * the classic proposal card. Discount + membership savings are derived from the
 * engine-persisted amounts (or re-derived from the discount config for older rows)
 * exactly as `OptionCard` does, so the two never disagree.
 */
export interface ProposalOptionBreakdown {
  subtotal: number;
  discount: number;
  membershipSavings: number;
  tax: number;
  total: number;
  /** Visible, priced lines (optional add-ons the customer kept are included). */
  lines: Array<{ name: string; price: number | null }>;
}

/** A visible line's customer price, or `null` when the tenant hid the price. */
function linePrice(line: ProposalLineItem): number | null {
  return line.displayConfig?.hidePrice ? null : (line.lineListTotal ?? line.lineTotal ?? 0);
}

/** Named customer lines split into required (always in the price) and optional
 *  add-ons the customer kept — the single-scope cards group them like the classic. */
export function splitProposalLines(option: ProposalOption): {
  required: ProposalLineItem[];
  optional: ProposalLineItem[];
} {
  // Built from the offer split, not a flat `isOptional` partition: a declined
  // service's parts must not leak into "What's included", and a bundle part must
  // ride with its service rather than list itself. Only revenue-charging named
  // customer lines belong on the checklist (`rendersOnCustomerDocument &&
  // chargesRevenue`), the same gate the classic option card applies.
  const named = (l: ProposalLineItem) =>
    rendersOnCustomerDocument(l) && chargesRevenue(l) && (l.name ?? '').trim().length > 0;
  const split = splitOptionalOffers(option.lineItems ?? []);
  return {
    // Once the customer has chosen a membership, its frozen sale and setup
    // fee belong to the scope too. The offer block is no longer displayed.
    required: [
      ...split.required,
      ...split.offers
        .filter((o) => isMembershipLine(o.parent) && o.parent.isSelected !== false)
        .flatMap((o) => offerCountedLines(o, true)),
    ].filter(named),
    // The taken offers' counted lines — a declined offer contributes nothing.
    optional: split.offers
      .filter((o) => !isMembershipLine(o.parent) && o.parent.isSelected !== false)
      .flatMap((o) => offerCountedLines(o, true))
      .filter(named),
  };
}

/** The per-option deposit due — charged on the deposit-ELIGIBLE total (membership
 *  plan lines excluded), mirroring the classic card and the estimate the option
 *  materialises into, so a membership-priced option never inflates the deposit. */
export function proposalOptionDeposit(option: ProposalOption): number {
  return proposalOptionDepositAmountOn(option, getProposalOptionDepositEligibleTotal(option));
}

/**
 * Re-price ONE option for the customer's current optional picks — a live preview
 * that mirrors what the server re-computes at accept. Each non-membership
 * optional's `isSelected` is set from `selectedKeys`, then the option's
 * subtotal/tax/total/discount are recomputed from the selected lines' own
 * amounts (`resolveEstimateLineAmounts`), with the document discount re-applied
 * to the new base. Membership/required lines are untouched; the authoritative
 * figure is still the server's on accept.
 */
export function proposalOptionWithSelection(
  option: ProposalOption,
  selectedKeys: ReadonlySet<string>,
): ProposalOption {
  const lines = option.lineItems ?? [];
  const split = splitOptionalOffers(lines);
  // The customer key set describes editable offers only. Hidden or unnamed
  // optional scope keeps its stored selection, matching the partial API patch.
  const offeredKeys = new Set(proposalOptionOffers(option).map((offer) => offer.key));
  const isSelected = (parent: ProposalLineItem, index: number) => {
    const key = optionalKey(parent, index);
    return offeredKeys.has(key) ? selectedKeys.has(key) : parent.isSelected !== false;
  };
  // The lines currently in the sale: required work plus every taken offer's
  // counted lines (parent + billed parts). A declined offer's parts drop with
  // it — the estimate's `offerCountedLines` rule verbatim — so the preview total
  // matches what the server re-prices and materialises. Membership follows its
  // OWN stored selection here; the join-&-save toggle owns that number.
  const selectedLines = [
    ...split.required,
    ...split.offers.flatMap((offer, index) => {
      const on = isSelected(offer.parent, index);
      return offerCountedLines(offer, on);
    }),
  ];
  const inSale = new Set(selectedLines);
  const amounts = selectedLines.map((l) => resolveEstimateLineAmounts(l));
  const subtotal = round2(amounts.reduce((s, a) => s + a.lineSubtotal, 0));
  const tax = round2(amounts.reduce((s, a) => s + a.lineTax, 0));
  const discount =
    option.discountType === 'PERCENT'
      ? round2((subtotal * (option.discountValue ?? 0)) / 100)
      : option.discountType === 'FLAT'
        ? Math.min(option.discountValue ?? 0, subtotal)
        : 0;
  const total = round2(subtotal - discount + tax);
  // Reflect the sale on each OPTIONAL non-membership line's `isSelected` so the
  // document renders the toggle state; a non-optional part keeps its own flag,
  // exactly as `customerApprove` treats it — the engine cascades a declined
  // service's parts, they are never written deselected.
  const lineItems = lines.map((line) =>
    line.isOptional === true && !isMembershipLine(line)
      ? { ...line, isSelected: inSale.has(line) }
      : line,
  );
  // The join-&-save offer was frozen on the option's DEFAULT picks. Once the
  // customer changes an add-on, rebase the offer's member totals onto the lines
  // now in the sale (the quote page's rule for estimates) so the headline price,
  // savings and approve recap describe the scope being bought — the server
  // reprices that same scope authoritatively on accept. An unchanged selection
  // keeps the frozen figures untouched: they are the exact tamper check.
  const selectionChanged = split.offers.some((offer, index) => {
    return isSelected(offer.parent, index) !== (offer.parent.isSelected !== false);
  });
  const frozenOffer = option.membershipPlanSnapshot?.offer ?? null;
  let membershipPlanSnapshot = option.membershipPlanSnapshot;
  if (selectionChanged && frozenOffer?.planPrice != null) {
    const workLines = selectedLines.filter((line) => !isMembershipLine(line));
    let rebasedOffer = membershipOfferForSelectedLines(
      frozenOffer,
      // Proposal lines carry the same money/identity fields the offer
      // adjustments are matched on (lineKey, lineItemId, totals).
      workLines as unknown as EstimateLineItem[],
      lines as unknown as EstimateLineItem[],
    );
    const workAmounts = workLines.map(resolveEstimateLineAmounts);
    if (
      frozenOffer.savings === 0 &&
      frozenOffer.memberGrandTotal === frozenOffer.nonMemberGrandTotal &&
      !frozenOffer.selectionPricing &&
      !frozenOffer.lineAdjustments?.length &&
      workAmounts.every((amount) => amount.lineTax === 0)
    ) {
      // Older zero-savings offers need no member-rate reconstruction, but
      // adding raw quoted line totals loses the document discount on an add-on.
      // Reprice work only; membership/setup charges are added by the offer UI.
      // Taxed or member-discounted legacy scope still needs its frozen context.
      const workSubtotal = round2(
        workAmounts.reduce((sum, amount) => sum + amount.lineSubtotal, 0),
      );
      const workDiscount =
        option.discountType === 'PERCENT'
          ? round2((workSubtotal * (option.discountValue ?? 0)) / 100)
          : option.discountType === 'FLAT'
            ? Math.min(option.discountValue ?? 0, workSubtotal)
            : 0;
      const workTotal = round2(Math.max(0, workSubtotal - workDiscount));
      rebasedOffer = {
        ...frozenOffer,
        nonMemberGrandTotal: workTotal,
        memberGrandTotal: workTotal,
        savings: 0,
      };
    }
    membershipPlanSnapshot = { ...option.membershipPlanSnapshot, offer: rebasedOffer };
  }
  return {
    ...option,
    lineItems,
    membershipPlanSnapshot,
    subtotal,
    taxTotal: tax,
    total,
    discountAmount: discount,
  };
}

export function proposalOptionBreakdown(option: ProposalOption): ProposalOptionBreakdown {
  const persisted = option.discountAmount;
  const discount = Number.isFinite(persisted)
    ? (persisted as number)
    : option.discountType === 'PERCENT'
      ? Math.round(((option.subtotal * (option.discountValue ?? 0)) / 100) * 100) / 100
      : option.discountType === 'FLAT'
        ? (option.discountValue ?? 0)
        : 0;
  const membershipSavings =
    Math.round((option.subtotal + option.taxTotal - option.total - discount) * 100) / 100;

  // The priced rows: required work + the taken offers' counted lines, so a
  // declined service's parts never print and a bundle reconciles to the total.
  const split = splitOptionalOffers(option.lineItems ?? []);
  const visible = [
    ...split.required,
    ...split.offers
      .filter((o) => o.parent.isSelected !== false)
      .flatMap((o) => offerCountedLines(o, true)),
  ].filter((li) => rendersOnCustomerDocument(li) && chargesRevenue(li));

  return {
    subtotal: option.subtotal,
    discount,
    membershipSavings,
    tax: option.taxTotal,
    total: option.total,
    lines: visible.map((li) => ({ name: li.name, price: linePrice(li) })),
  };
}
